import { useState, useEffect } from 'react';

export type DeviceType =
    | {
          isMobile: 'mobile' | 'desktop';
          isAndroid: boolean;
      }
    | undefined;

export interface DeviceDetectionResult {
    deviceType: DeviceType | undefined;
    isMobile: boolean;
    isDesktop: boolean;
    isAndroid: boolean;
}

/**
 * Helper function to determine device type based on user agent
 */
export function getDeviceType(): DeviceType | undefined {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
    }
    const userAgent = navigator?.userAgent || (window as any).opera;
    const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];

    const isMobile = toMatch.some((toMatchItem) => {
        return userAgent.match(toMatchItem);
    });

    const isAndroid = /Android/i.test(userAgent) || /Windows Phone/i.test(userAgent) || /BlackBerry/i.test(userAgent);

    return {
        isMobile: isMobile ? 'mobile' : 'desktop',
        isAndroid,
    };
}

/**
 * Custom hook to detect the device type based on browser user agent
 * @returns {DeviceDetectionResult} Object containing device type and boolean flags
 */
export function useDeviceDetection(): DeviceDetectionResult {
    const [deviceType, setDeviceType] = useState<DeviceType | undefined>(() => {
        // Server-side rendering fallback
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
            return undefined;
        }
        return getDeviceType();
    });

    useEffect(() => {
        // Skip if window is not available (SSR)
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
            return;
        }

        // Set device type based on user agent
        setDeviceType(getDeviceType());
    }, []);

    return {
        deviceType,
        isMobile: deviceType?.isMobile === 'mobile',
        isDesktop: deviceType?.isMobile === 'desktop',
        isAndroid: deviceType?.isAndroid === true,
    };
}
